// react
import { useState } from 'react';
import { useDispatch } from 'react-redux';

// internationalization
import { useTranslation } from 'react-i18next';

// tanstack
import { Link, useNavigate } from '@tanstack/react-router';

// material ui icons
import BarChartRoundedIcon from '@mui/icons-material/BarChartRounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import LocalOfferRoundedIcon from '@mui/icons-material/LocalOfferRounded';
import RssFeedRoundedIcon from '@mui/icons-material/RssFeedRounded';
import SpeedRoundedIcon from '@mui/icons-material/SpeedRounded';

// material ui
import {
  Box,
  Chip,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGridPremium } from '@mui/x-data-grid-premium';

// shared components
import DataGridNoRowsCustomOverlay from '@/components/ui/datagrid/DataGridNoRowsOverlay';
import { DataGridToolbar } from '@/components/ui/datagrid/DataGridToolbar';
import RowActions from '@/components/ui/datagrid/RowActions';

// components
import { useDatagridLocaleText } from '@/hooks/useDatagridLocaleText';
import { useLocalStorageJson } from '@/hooks/useLocalStorageJson';
import { showSnackbar } from '@/redux/snackbarSlice';

// relative
import DeleteUnitDialog from './DeleteUnitDialog';

const getSensor = (sensors) => {
  const sensorWithId = sensors?.find((sensor) => sensor.Sensor?.SensorId);

  if (sensorWithId) {
    return sensorWithId.Sensor.SensorId;
  }

  const hasApiInput = sensors?.find((sensor) => sensor?.ApiInput === true);
  if (hasApiInput) {
    return 'API Input';
  }

  return null;
};

const getTags = (tags) => {
  return (tags ?? [])
    .map((t) => (typeof t === 'string' ? t : (t?.Text ?? '')).trim())
    .filter(Boolean)
    .join(', ');
};

const UnitDataGrid = (props) => {
  const { t } = useTranslation('units-gateways-sensors');
  const { units, isLoading } = props;
  const dispatch = useDispatch();

  const [rowModesModel, setRowModesModel] = useState({});
  // const [selectedRow, setSelectedRow] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [unitToDelete, setUnitToDelete] = useState(null);
  const navigate = useNavigate();

  const rows = units?.map((unit) => ({
    id: unit?.UnitId,
    Account: unit?.Account,
    UnitId: unit?.UnitId,
    Name: unit?.Name,
    Description: unit?.Description,
    Sensor: getSensor(unit?.UnitSensors) ?? null,
    Tags: getTags(unit?.Tags),
    TagsArray: Array.isArray(unit?.Tags) ? unit.Tags : [],
    RecordedDataTypes: unit?.RecordedDataTypes,
    UnitSensors: unit?.UnitSensors,
    UnitType: unit?.UnitType,
  }));

  const handleRowModesModelChange = (newRowModesModel) => {
    setRowModesModel(newRowModesModel);
  };

  const handleViewUnit = (unit) => {
    navigate({ to: `${unit.UnitId}` });
  };

  const handleEditUnit = (unit) => {
    navigate({ to: `${unit.UnitId}/edit` });
  };

  const handleDeleteUnit = (unit) => {
    setUnitToDelete(unit);
    setDeleteDialogOpen(true);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setUnitToDelete(null);
  };

  const [dataGridState, setDataGridState] = useLocalStorageJson(
    'Unit-DataGrid-Presets',
    {
      presets: [{ name: 'Default', visibilityList: {}, density: 'standard' }],
      chosenPreset: 0,
    },
  );

  const handleVisibilityChange = (model) => {
    setDataGridState((prev) => ({
      ...prev,
      presets: prev.presets.map((p, i) =>
        i === prev.chosenPreset ? { ...p, visibilityList: model } : p,
      ),
    }));
  };

  const handleDensityChange = (density) => {
    setDataGridState((prev) => ({
      ...prev,
      presets: prev.presets.map((p, i) =>
        i === prev.chosenPreset ? { ...p, density } : p,
      ),
    }));
  };

  const handleCopyUnitId = async (unit) => {
    if (!unit?.UnitId) return;
    try {
      await navigator.clipboard.writeText(unit.UnitId);
      dispatch(
        showSnackbar({
          message:
            t?.('units.unitIdCopied', { id: unit.UnitId }) ||
            `Unit ID copied: ${unit.UnitId}`,
          severity: 'success',
        }),
      );
    } catch (err) {
      console.error('Failed to copy Unit ID:', err);
      dispatch(
        showSnackbar({
          message: t?.('units.unitIdCopyFailed') || 'Failed to copy Unit ID',
          severity: 'error',
        }),
      );
    }
  };

  const renderSensorCell = (sensorValue, row) => {
    const isApiInput = sensorValue === 'API Input';
    const sensorId = isApiInput ? null : row.UnitSensors[0]?.Sensor?.SensorId;
    const externalSensorKey =
      !sensorId && !isApiInput
        ? row.UnitSensors[0]?.ExternalSensor?.Device?.Key
        : null;

    const label = sensorValue ? sensorValue : externalSensorKey || 'No Sensors';
    const isNoSensors = label === 'No Sensors';
    const isNavigable = sensorValue && !isApiInput;

    if (isNoSensors) {
      return (
        <Stack direction="row" alignItems="center" spacing={1} height="100%">
          <Typography
            variant="body2"
            component="span"
            sx={{
              color: 'text.disabled',
              fontStyle: 'italic',
            }}
          >
            {label}
          </Typography>
        </Stack>
      );
    }

    const IconComponent = isApiInput ? RssFeedRoundedIcon : SpeedRoundedIcon;

    return (
      <Stack direction="row" alignItems="center" spacing={1} height="100%">
        <IconComponent fontSize="small" sx={{ color: 'primary.main' }} />
        <Typography
          variant="body2"
          component={isNavigable ? Link : 'span'}
          to={isNavigable ? `/$accountId/sensors/${sensorValue}` : undefined}
          color="primary"
          sx={{
            ':hover': { textDecoration: isNavigable ? 'underline' : 'none' },
          }}
        >
          {label}
        </Typography>
      </Stack>
    );
  };

  const columns = [
    {
      field: 'UnitId',
      headerName: 'Unit ID',
      width: 150,
      renderCell: (params) => {
        return (
          <Stack
            direction="row"
            alignItems="center"
            spacing={0.5}
            sx={{
              height: '100%',
              width: '100%',
              '& .copy-button': {
                opacity: 0,
                transition: 'opacity 0.2s ease',
              },
              '&:hover .copy-button': {
                opacity: 1,
              },
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {params?.row?.UnitId || 'Not Available'}
            </Typography>
            <IconButton
              className="copy-button"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleCopyUnitId(params.row);
              }}
              sx={{
                color: 'text.secondary',
                '&:hover': {
                  color: 'primary.main',
                },
              }}
            >
              <ContentCopyRoundedIcon fontSize="small" />
            </IconButton>
          </Stack>
        );
      },
    },
    {
      field: 'Name',
      headerName: t('units.name'),
      flex: 1,
      renderCell: (params) => {
        const name = params?.row?.Name || 'Not Available';
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <Typography variant="body2">{name}</Typography>
          </Box>
        );
      },
    },
    {
      field: 'Sensor',
      headerName: 'Sensor',
      width: 200,
      renderCell: (params) => {
        const sensorValue = params.value;
        return renderSensorCell(sensorValue, params.row);
      },
    },
    {
      field: 'Tags',
      headerName: 'Tag',
      flex: 1,
      groupable: false,
      getQuickFilterText: (value) => String(value ?? ''),
      renderCell: (params) => {
        const tags = Array.isArray(params?.row?.TagsArray)
          ? params.row.TagsArray
          : [];
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <Stack
              direction="row"
              spacing={0.5}
              alignItems="flex-start"
              alignContent="flex-start"
              flexWrap="wrap"
              useFlexGap
              sx={{ width: '100%', py: 0.5 }}
            >
              {tags.length > 0
                ? tags.map((tag, i) => (
                    <Chip
                      icon={
                        <LocalOfferRoundedIcon fontSize="small" color="white" />
                      }
                      key={tag?.TagId ?? i}
                      label={typeof tag === 'string' ? tag : (tag?.Text ?? '')}
                      size="small"
                      sx={{
                        borderRadius: 1,
                        ml: 0.5,
                        mb: 0.5,
                        backgroundColor: 'info.main',
                        color: '#fff',
                      }}
                    />
                  ))
                : null}
            </Stack>
          </Box>
        );
      },
    },
    {
      field: 'actions',
      headerName: t('units.actions'),
      width: 150,
      sortable: false,
      filterable: false,
      hideable: false,
      renderCell: (params) => {
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <Tooltip
              title={t('units.viewData', {
                name: params?.row?.Name || params?.row?.UnitId || '',
              })}
              placement="top"
            >
              <span>
                <IconButton
                  size="small"
                  component={Link}
                  to={`/$accountId/view-data?units=${params.row.UnitId}`}
                  // onClick={stop(onView)}
                  // disabled={disabled}
                  data-testid="row-action-view"
                  sx={{ borderRadius: 2 }}
                >
                  <BarChartRoundedIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            {/* <Divider orientation="vertical" flexItem sx={{ m: 1 }} /> */}
            <RowActions
              row={params.row}
              onView={() => handleViewUnit(params.row)}
              onEdit={() => handleEditUnit(params.row)}
              onDelete={() => handleDeleteUnit(params.row)}
            />
          </Box>
        );
      },
    },
  ];

  const localeText = useDatagridLocaleText();

  return (
    <>
      <Box data-tour="unit-overview-grid">
        <DataGridPremium
          showToolbar
          rows={rows ?? []}
          columns={columns}
          getRowHeight={() => 'auto'}
          getEstimatedRowHeight={() => 84}
          rowModesModel={rowModesModel}
          onRowModesModelChange={handleRowModesModelChange}
          noRowsOverlay={DataGridNoRowsCustomOverlay}
          isLoading={isLoading}
          rowsName="Units"
          localeText={localeText}
          pagination
          initialState={{
            pagination: { paginationModel: { page: 0, pageSize: 50 } },
          }}
          pageSizeOptions={[50, 100]}
          density={dataGridState.presets[dataGridState.chosenPreset].density}
          onDensityChange={handleDensityChange}
          onColumnVisibilityModelChange={handleVisibilityChange}
          columnVisibilityModel={{
            ...dataGridState.presets[dataGridState.chosenPreset].visibilityList,
          }}
          slots={{
            toolbar: DataGridToolbar,
          }}
          slotProps={{
            toolbar: {
              showDivider: false,
              showFilters: true,
              showColumns: true,
            },
          }}
          disableRowSelectionOnClick
          sx={{
            '& .MuiDataGrid-row': {
              minHeight: '52px !important',
            },
            '& .MuiDataGrid-cell': {
              minHeight: '52px !important',
            },
            '& .MuiDataGrid-cell[data-field="Tags"]': {
              alignItems: 'flex-start',
            },
          }}
        />
      </Box>

      <DeleteUnitDialog
        open={deleteDialogOpen}
        unit={unitToDelete}
        onClose={handleDeleteCancel}
      />
    </>
  );
};

export default UnitDataGrid;
